<template>
  <div class="space-y-6">
    <h1 class="text-3xl font-bold text-neutral-900 dark:text-neutral-100">HomeBase Users</h1>

    <!-- Create User Form -->
    <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft p-6">
      <h2 class="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-6">Create New User</h2>
      <div class="flex flex-wrap items-center gap-4">
        <input
          class="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 dark:placeholder:text-neutral-400 transition-all duration-200"
          type="text"
          placeholder="Username"
          aria-label="Username"
          autocomplete="username"
          v-model="newUser.username"
        />
        <input
          class="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 dark:placeholder:text-neutral-400 transition-all duration-200"
          type="password"
          placeholder="Password"
          aria-label="Password"
          autocomplete="new-password"
          minlength="8"
          v-model="newUser.password"
        />
        <button
          class="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-800 transition-colors duration-200 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          @click="createUser"
          :disabled="!newUser.username || !newUser.password || creating"
        >
          <svg v-if="creating" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {{ creating ? 'Creating…' : 'Create User' }}
        </button>
      </div>
    </div>

    <!-- Users List -->
    <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft p-6">
      <h2 class="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-6">User Management</h2>
      <div class="flex flex-wrap items-center gap-4 mb-6">
        <button
          @click="reload"
          class="px-4 py-2 bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 rounded-lg hover:bg-neutral-200 dark:hover:bg-neutral-600 transition-colors duration-200 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
        >
          Reload
        </button>
      </div>

      <AlertBanner
        v-if="error && users.length"
        type="error"
        message="Couldn't refresh users — showing the last loaded list."
        :dismissible="false"
        class="mb-4"
      />
      <AlertBanner
        v-else-if="!loading && limitReached"
        type="info"
        :message="`Showing the first ${users.length} users.`"
        :dismissible="false"
        class="mb-4"
      />

      <!-- Loading State -->
      <div v-if="loading" class="flex items-center justify-center py-12">
        <div class="flex flex-col items-center gap-3">
          <svg class="animate-spin h-10 w-10 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p class="text-sm text-neutral-600 dark:text-neutral-400">Loading users...</p>
        </div>
      </div>

      <!-- Error State (nothing loaded yet) -->
      <ErrorState
        v-else-if="error && !users.length"
        title="Couldn't load users"
        :message="error"
        @retry="reload"
      />

      <!-- Empty State -->
      <div v-else-if="!sortedAndFiltered.length" class="py-12 text-center">
        <div class="flex flex-col items-center gap-3">
          <span class="material-icons text-5xl text-neutral-400 dark:text-neutral-600" aria-hidden="true">inbox</span>
          <p class="text-neutral-600 dark:text-neutral-400">No users found</p>
        </div>
      </div>

      <!-- Data Table -->
      <div v-else class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-neutral-50 dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-700">
            <tr>
              <th :aria-sort="ariaSort('username')" class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <button type="button" @click="sortBy('username')" class="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors">
                  Username
                  <span v-if="sortState.key === 'username'" class="material-icons text-sm leading-none" aria-hidden="true">{{ sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                </button>
              </th>
              <th :aria-sort="ariaSort('createdAt')" class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <button type="button" @click="sortBy('createdAt')" class="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors">
                  Created
                  <span v-if="sortState.key === 'createdAt'" class="material-icons text-sm leading-none" aria-hidden="true">{{ sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                </button>
              </th>
              <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-neutral-200 dark:divide-neutral-700">
            <tr v-for="user in sortedAndFiltered" :key="user.id" class="hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors">
              <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">{{ user.username }}</td>
              <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">{{ (user.createdAt || '').slice(0,10) }}</td>
              <td class="px-4 py-3 text-sm">
                <div class="flex items-center gap-2">
                  <button
                    class="text-sm px-3 py-1.5 bg-yellow-500 text-yellow-950 rounded-lg hover:bg-yellow-600 transition-colors duration-200 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 focus-visible:ring-offset-2"
                    @click="editPassword(user)"
                  >
                    Change Password
                  </button>
                  <button
                    class="text-sm px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                    @click="deleteUser(user)"
                  >
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="flex items-center gap-4 mt-6">
        <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-neutral-100 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-200">
          Total Users: {{ users.length }}
        </span>
      </div>
    </div>

    <!-- Change Password Modal -->
    <Modal
      :show="showPasswordModal"
      :title="`Change Password - ${selectedUser?.username || ''}`"
      size="md"
      :closable="!updatingPassword"
      @close="closePasswordModal"
    >
      <div class="space-y-4">
        <div>
          <label for="hb-new-password" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">New Password</label>
          <input
            id="hb-new-password"
            class="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 dark:placeholder:text-neutral-400 transition-all duration-200"
            type="password"
            v-model="newPassword"
            placeholder="Enter new password"
            autocomplete="new-password"
            minlength="8"
          />
          <p class="mt-1.5 text-xs text-neutral-500 dark:text-neutral-400">Must be at least 8 characters.</p>
        </div>
        <div>
          <label for="hb-confirm-password" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Confirm Password</label>
          <input
            id="hb-confirm-password"
            class="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 dark:placeholder:text-neutral-400 transition-all duration-200"
            type="password"
            v-model="confirmPassword"
            placeholder="Confirm new password"
            autocomplete="new-password"
            minlength="8"
          />
        </div>
        <div v-if="passwordError" role="alert" class="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-800 dark:text-red-300">
          {{ passwordError }}
        </div>
      </div>

      <template #footer>
        <div class="flex items-center justify-end gap-3">
          <Button variant="secondary" :disabled="updatingPassword" @click="closePasswordModal">
            Cancel
          </Button>
          <Button
            variant="primary"
            :loading="updatingPassword"
            :disabled="!newPassword || !confirmPassword"
            @click="updatePassword"
          >
            Update Password
          </Button>
        </div>
      </template>
    </Modal>

    <!-- Delete Confirmation -->
    <ConfirmDialog
      :show="showDeleteModal"
      title="Delete User"
      :message="`Are you sure you want to delete user ${selectedUser?.username || ''}? This action cannot be undone.`"
      confirm-label="Delete User"
      :loading="deleting"
      danger
      @confirm="confirmDelete"
      @cancel="closeDeleteModal"
    />
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref } from 'vue'
import useHomebaseUsers from '@/composables/useHomebaseUsers'
import useToast from '@/composables/useToast'
import Modal from '@/components/ui/Modal.vue'
import Button from '@/components/ui/Button.vue'
import ConfirmDialog from '@/components/ui/ConfirmDialog.vue'
import ErrorState from '@/components/ui/ErrorState.vue'
import AlertBanner from '@/components/ui/AlertBanner.vue'

export default {
  name: 'HomeBaseUsers',
  components: { Modal, Button, ConfirmDialog, ErrorState, AlertBanner },
  setup() {
    const {
      users,
      loading,
      error,
      params,
      fetchUsers,
      createUser: createUserAPI,
      updateUserPassword,
      deleteUser: deleteUserAPI
    } = useHomebaseUsers()

    const { showToast } = useToast()

    const newUser = reactive({
      username: '',
      password: ''
    })

    const showPasswordModal = ref(false)
    const showDeleteModal = ref(false)
    const selectedUser = ref(null)
    const newPassword = ref('')
    const confirmPassword = ref('')
    const passwordError = ref('')
    const creating = ref(false)
    const updatingPassword = ref(false)
    const deleting = ref(false)

    const reload = () => {
      fetchUsers()
    }

    const sortState = reactive({ key: 'createdAt', dir: 'desc' })
    const sortBy = (key) => {
      if (sortState.key === key) {
        sortState.dir = sortState.dir === 'asc' ? 'desc' : 'asc'
      } else {
        sortState.key = key
        sortState.dir = 'asc'
      }
    }

    const ariaSort = (key) =>
      sortState.key === key ? (sortState.dir === 'asc' ? 'ascending' : 'descending') : 'none'

    const sortedAndFiltered = computed(() => {
      return [...users.value].sort((a, b) => {
        const va = (a[sortState.key] || '')
        const vb = (b[sortState.key] || '')
        if (va < vb) return sortState.dir === 'asc' ? -1 : 1
        if (va > vb) return sortState.dir === 'asc' ? 1 : -1
        return 0
      })
    })

    const limitReached = computed(() => users.value.length >= (params.value.limit || 100))

    const createUser = async () => {
      if (!newUser.username || !newUser.password || creating.value) return

      if (newUser.password.length < 8) {
        showToast('error', 'Password must be at least 8 characters')
        return
      }

      creating.value = true
      try {
        await createUserAPI({
          username: newUser.username,
          password: newUser.password
        })
        newUser.username = ''
        newUser.password = ''
        showToast('success', 'User created')
      } catch (err) {
        showToast('error', 'Failed to create user. Please try again.')
      } finally {
        creating.value = false
      }
    }

    const editPassword = (user) => {
      selectedUser.value = user
      newPassword.value = ''
      confirmPassword.value = ''
      passwordError.value = ''
      showPasswordModal.value = true
    }

    const updatePassword = async () => {
      if (!newPassword.value || !confirmPassword.value || updatingPassword.value) return

      if (newPassword.value.length < 8) {
        passwordError.value = 'Password must be at least 8 characters'
        return
      }

      if (newPassword.value !== confirmPassword.value) {
        passwordError.value = 'Passwords do not match'
        return
      }

      updatingPassword.value = true
      try {
        await updateUserPassword(selectedUser.value.id, newPassword.value)
        closePasswordModal()
        showToast('success', 'Password updated')
      } catch (err) {
        passwordError.value = err.message
        showToast('error', 'Failed to update password. Please try again.')
      } finally {
        updatingPassword.value = false
      }
    }

    const deleteUser = (user) => {
      selectedUser.value = user
      showDeleteModal.value = true
    }

    const confirmDelete = async () => {
      if (deleting.value) return
      deleting.value = true
      try {
        await deleteUserAPI(selectedUser.value.id)
        closeDeleteModal()
        showToast('success', 'User deleted')
      } catch (err) {
        showToast('error', 'Failed to delete user. Please try again.')
      } finally {
        deleting.value = false
      }
    }

    const closePasswordModal = () => {
      showPasswordModal.value = false
      selectedUser.value = null
      newPassword.value = ''
      confirmPassword.value = ''
      passwordError.value = ''
    }

    const closeDeleteModal = () => {
      showDeleteModal.value = false
      selectedUser.value = null
    }

    onMounted(() => {
      fetchUsers()
    })

    return {
      users,
      loading,
      error,
      newUser,
      showPasswordModal,
      showDeleteModal,
      selectedUser,
      newPassword,
      confirmPassword,
      passwordError,
      creating,
      updatingPassword,
      deleting,
      reload,
      sortedAndFiltered,
      sortBy,
      sortState,
      ariaSort,
      limitReached,
      createUser,
      editPassword,
      updatePassword,
      deleteUser,
      confirmDelete,
      closePasswordModal,
      closeDeleteModal
    }
  }
}
</script>
